import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Stack, TextField, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { TablesColum } from '@/types';

type Props = {
  options: TablesColum[];
  label?: string;
  mode: 'ReadOnly' | 'Edit';
  existingKeys?: string[];
  helpText?: string | null;
  onAdd: (key: string, displayName: string) => void;
};

const FieldAdder = ({
  options,
  label = 'Select field',
  mode,
  existingKeys = [],
  helpText = null,
  onAdd,
}: Props) => {
  const [selected, setSelected] = useState<TablesColum | null>(null);
  const [displayName, setDisplayName] = useState<string>('');

  const opts = useMemo(() => options ?? [], [options]);
  const alreadyAdded = !!selected && existingKeys.includes((selected as TablesColum).key);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Autocomplete
          sx={{ minWidth: 220 }}
          size="small"
          options={opts}
          getOptionLabel={(option: TablesColum) => option?.key ?? option?.name ?? ''}
          value={selected}
          onChange={(_e, value) => {
            setSelected(value as TablesColum | null);
            setDisplayName(value ? (value as TablesColum).key : '');
          }}
          renderInput={(params) => <TextField {...params} label={label} />}
          disabled={mode === 'ReadOnly'}
        />

        <TextField
          size="small"
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        <Button
          variant="outlined"
          onClick={() => {
            if (!selected) return;
            onAdd(selected.key, displayName || selected.name || selected.key);
            setSelected(null);
            setDisplayName('');
          }}
          disabled={mode === 'ReadOnly' || !selected || alreadyAdded}
          startIcon={<Add />}
        >
          {alreadyAdded ? 'Added' : 'Add'}
        </Button>
      </Stack>
      {helpText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {helpText}
        </Typography>
      )}
    </Box>
  );
};

export default FieldAdder;
